import React, { useState, useEffect, useRef, useCallback, type ReactElement, type ReactNode } from "react";
import { debounce } from "lodash";
import { type SegmentProps } from "./Segment/Segment";

const MORE_BUTTON_WIDTH = 92;
const MIN_VISIBLE_COUNT = 1;
const GAP = 20;

interface UseSegmentCalculationsResult {
  containerRef: React.RefObject<HTMLDivElement>;
  visibleSegments: ReactElement<SegmentProps>[];
  overflowSegments: ReactElement<SegmentProps>[];
}

export function useSegmentCalculations(children: ReactNode): UseSegmentCalculationsResult {
  const containerRef = useRef<HTMLDivElement>(null);

  // State for tracking measurements and segments
  const [containerWidth, setContainerWidth] = useState(0);
  const [segmentWidths, setSegmentWidths] = useState<number[]>([]);
  const [validSegments, setValidSegments] = useState<ReactElement<SegmentProps>[]>([]);
  const [visibleSegments, setVisibleSegments] = useState<ReactElement<SegmentProps>[]>([]);
  const [overflowSegments, setOverflowSegments] = useState<ReactElement<SegmentProps>[]>([]);
  const [initialMeasurementComplete, setInitialMeasurementComplete] = useState(false);

  // Process children to get valid segment elements
  useEffect(() => {
    const segments = React.Children.toArray(children).filter((child): child is ReactElement<SegmentProps> =>
      React.isValidElement(child),
    );

    setValidSegments(segments);

    if (!initialMeasurementComplete) {
      setVisibleSegments(segments); // Initially show all segments until we measure them
      setOverflowSegments([]);
    }
  }, [children, initialMeasurementComplete]);

  // Set up resize observer to track container width changes
  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const newWidth = entry.contentRect?.width ?? 0;
        setContainerWidth(newWidth);
      }
    });

    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  // Create a map of segment elements to their measured widths
  const measureSegmentWidths = useCallback(() => {
    // Use a virtual measurement technique
    const measureDiv = document.createElement("div");
    measureDiv.style.position = "absolute";
    measureDiv.style.visibility = "hidden";
    measureDiv.style.display = "flex";
    measureDiv.style.width = containerWidth ? `${containerWidth}px` : "100%";
    measureDiv.className = "segment-control";
    document.body.appendChild(measureDiv);

    const widths: number[] = [];

    validSegments.forEach((segment) => {
      const segmentDiv = document.createElement("div");
      segmentDiv.className = "segment";
      segmentDiv.textContent = segment.props.label || "";
      measureDiv.appendChild(segmentDiv);
      widths.push(segmentDiv.getBoundingClientRect().width);
    });

    document.body.removeChild(measureDiv);
    setSegmentWidths(widths);
  }, [containerWidth, validSegments]);

  // Measure segments when they change
  useEffect(() => {
    if (validSegments.length === 0) return;

    // Use setTimeout to ensure DOM is ready for measurement
    const timer = setTimeout(() => {
      measureSegmentWidths();
    }, 0);

    return () => clearTimeout(timer);
  }, [validSegments, measureSegmentWidths]);

  useEffect(() => {
    if (segmentWidths.length > 0 && !initialMeasurementComplete) {
      setInitialMeasurementComplete(true);
    }
  }, [segmentWidths, initialMeasurementComplete]);

  // Calculate which segments fit and which overflow
  const calculateVisibleSegments = useCallback(() => {
    if (containerWidth === 0 || segmentWidths.length === 0 || !initialMeasurementComplete) return;

    let availableWidth = containerWidth;
    let visibleCount = 0;

    let computedGap = GAP;
    if (containerRef.current) {
      const computedStyle = window.getComputedStyle(containerRef.current);
      const gapValue = parseInt(computedStyle.gap, 10);
      computedGap = !isNaN(gapValue) ? gapValue : GAP;
    }

    const totalSegmentsWidth = segmentWidths.reduce((sum: number, width: number) => sum + width, 0);
    const totalGapsWidth = (segmentWidths.length - 1) * computedGap; // n-1 gaps between n segments
    const totalWidth = totalSegmentsWidth + totalGapsWidth;

    const needsMoreButton = totalWidth > containerWidth;

    if (needsMoreButton) {
      availableWidth -= MORE_BUTTON_WIDTH + computedGap;
    }

    for (let i = 0; i < segmentWidths.length; i++) {
      const widthNeeded = segmentWidths[i] + computedGap;

      if (availableWidth >= widthNeeded) {
        availableWidth -= widthNeeded;
        visibleCount++;
      } else {
        break;
      }
    }

    visibleCount = Math.max(visibleCount, MIN_VISIBLE_COUNT);
    visibleCount = Math.min(visibleCount, validSegments.length);

    setVisibleSegments(validSegments.slice(0, visibleCount));
    setOverflowSegments(validSegments.slice(visibleCount));
  }, [containerWidth, segmentWidths, validSegments, initialMeasurementComplete]);

  // Recalculate when relevant values change
  useEffect(() => {
    if (!initialMeasurementComplete || containerWidth === 0) {
      return;
    }
    const debouncedCalculate = debounce(() => {
      calculateVisibleSegments();
    }, 100);
    debouncedCalculate();
    return () => debouncedCalculate.cancel();
  }, [calculateVisibleSegments, initialMeasurementComplete, containerWidth]);

  return { containerRef, visibleSegments, overflowSegments };
}
